import { getRouteApi } from '@tanstack/react-router';
import type { processDefinitionsTemplateQueryFragment$data } from './__generated__/processDefinitionsTemplateQueryFragment.graphql';
import type { TProcessDefinitionsTemplateSearchSchema } from './process-definitions-template-search-schema';

export const processDefinitionsTemplateRouteApi = getRouteApi(
    '/_index/process-definitions-template'
);

/**
 * It uses the search/query parameters of the location and the data from the query fragment to represent a list selection.
 * A validation function can be provided to check the entries of a list selection.
 * Returns undefined if a validation function is provided and a list selection contains an invalid entry.
 *
 * @param fragment A query fragment that contains data for a list
 * @param validate A validation function that checks the entries of a list selection
 */
export const useProcessDefinitionsTemplateSelectionState = (
    fragment?: processDefinitionsTemplateQueryFragment$data | null,
    validate?: (entry: processDefinitionsTemplateQueryFragment$data['content'][number]) => boolean
): TProcessDefinitionsTemplateSearchSchema['selected'] => {
    const selectedIds = processDefinitionsTemplateRouteApi.useSearch({
        select: (state) => state.selected,
    });

    if (!selectedIds || !fragment) {
        return undefined;
    }

    const selectedItems = fragment.content.filter((entry) => selectedIds.includes(entry.id));

    if (validate && !selectedItems.every(validate)) {
        return undefined;
    }

    return selectedItems.map((entry) => entry.id);
};

export const useProcessDefinitionsTemplateResetSelectionState = (): (() => void) => {
    const navigate = processDefinitionsTemplateRouteApi.useNavigate();
    return () => {
        // Navigate to the list and replace the current history entry to make it no longer accessible
        navigate({
            replace: true,
            search: (prevSearch) =>
                Object.assign(Object.assign({}, prevSearch), {
                    // remove selection
                    selected: undefined,
                }),
            to: '/process-definitions-template',
        });
    };
};

export const useProcessDefinitionsTemplateAnySelected = (
    selectionState?: TProcessDefinitionsTemplateSearchSchema['selected']
): boolean => Array.isArray(selectionState) && selectionState.length > 0;
